
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import { EntityConfigUtil } from "config/utils/entity-config.util";
import { every } from "lodash";
import { CheckboxInputComponent } from "rl-common/components/checkbox-input/checkbox-input.component";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormPlaceholderComponent } from "rl-common/components/form-placeholder/form-placeholder.component";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableCustomControlDirective } from "rl-common/components/form-table/form-table-custom-control.directive";
import { FormTableMessageDirective } from "rl-common/components/form-table/form-table-message.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { NumberInputComponent } from "rl-common/components/number-input/number-input.component";
import { TextAreaComponent } from "rl-common/components/text/text-area/text-area.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { TrimWhitespacePipe } from "rl-common/pipes/trim-whitespace.pipe";
import { ConstUtils } from "rl-common/rl-common.consts";
import { IAssocRolesLabels, IWfRole, IWfRoleStepAssociation, IWfStep, IWfStepSaveRequest } from "rl-common/services/company/company.models";
import { IWFActionConfig, IWFAssocAction, RevisionStatus } from "rl-common/services/entity-config/entity-config.models";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { GrowlerService } from "rl-common/services/growler.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { ProgressService } from "rl-common/services/progress.service";
import { SessionService } from "rl-common/services/session.service";
import { RevisionStatusUtil } from "rl-common/utils/revision-status.util";
import { Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";

interface IWorkflowStatusForm {
	statusId: FormControl<number>;
	statusName: FormControl<string>;
	statusDescription: FormControl<string>;
	activeIndicator: FormControl<number>;
	deleteIndicator: FormControl<number>;
	lockIndicator: FormControl<number>;
	systemIndicator: FormControl<number>;
	summationLockingIndicator: FormControl<number>;
	validActions: FormArray<FormControl<number>>;
	associatedRoles: FormArray<FormControl<IAssocRolesLabels>>;
	revisionStatus: FormControl<KeyValueOption>;
}

interface KeyValueOption {
	key: number;
	value: string;
}

@Component({
	selector: "rl-workflow-status-edit",
	styleUrls: ["./workflow-status-edit.component.scss"],
	templateUrl: "./workflow-status-edit.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TextInputComponent, ReactiveFormsModule, TextAreaComponent, DropdownSingleComponent, CheckboxInputComponent, NumberInputComponent, FormsModule, TrimWhitespacePipe, FormTableRowComponent, FormTableControlDirective, FormTableMessageDirective, FormTableCustomControlDirective, FormPlaceholderComponent]
})
export class WorkflowStatusEditComponent implements OnInit, OnDestroy {
	private readonly _fb = inject(FormBuilder);
	private readonly _entityConfigService = inject(EntityConfigService);
	private readonly _progressService = inject(ProgressService);
	private readonly _growlerService = inject(GrowlerService);
	private readonly _sessionService = inject(SessionService);
	private readonly _featureService = inject(FeatureService);
	private readonly _oneConfigService = inject(OneConfigService);

	_subs: Subscription[] = [];
	setStatusName: string;
	title: string;
	form: FormGroup<IWorkflowStatusForm>;
	actions: IWFActionConfig[] = [];
	roles: IWfRole[] = [];
	selectedValues: unknown[] = [];
	actionAssocs: IWFAssocAction[] = [];
	roleAssocs: IWfRoleStepAssociation[] = [];
	checkedActionIds: number[] = [];
	selectedAssocRoles: IAssocRolesLabels[];
	preselectedAssocRoles: IWfRole[] = [];
	formLoaded = false;
	isSaving = false;
	isSuperAdmin: boolean;
	unifiedWorkflow = FeatureKeys.UnifiedWorkflow;
	isUnifiedWorkflowActive = false;
	summationLockingEnabled = false;
	isConfigEditLocked: boolean = false;
	isGeneratedPaymentProcess: boolean = false;
	readonly LOCK_TOOLTIP = "This is required by the Generated Payment configuration and cannot be changed.";
	revisionStatuses: DropdownOptions<KeyValueOption> = {
		items: [],
		rowKeyFn: (type: KeyValueOption) => type.key,
		rowLabelFn: (type: KeyValueOption) => type.value
	};

	@Input()
	charTypeId: number;

	@Input()
	processId: number;

	@Input()
	statusId: number;

	@Input()
	wfStatusModel: IWfStep;

	@Input()
	summationLockingStepId: number;

	@Output()
	onComplete = new EventEmitter<boolean>();

	get isCreate() {
		return this.statusId === 0;
	}

	get associatedRolesIsEmpty() {
		return this.form.controls.associatedRoles && this.form.controls.associatedRoles.length === 0;
	}

	get associatedRolesFormControls() {
		return this.form.controls.associatedRoles && this.form.controls.associatedRoles.controls;
	}

	get allValidActionsSelected() {
		const allValidActionIds = this.actions && this.actions.map(a => a.actionID) || [];
		return every(allValidActionIds, id => this.checkedActionIds.includes(id));
	}

	ngOnInit() {
		const sub = this._sessionService.isRlAdmin$.pipe(
			take(1),
			tap(result => {
				this.isSuperAdmin = result;
				if (!this.isCreate) {
					this.statusId = this.wfStatusModel.stepID;
					this.setStatusName = this.wfStatusModel.stepName;
				}

				this.setTitle();
			}),
			switchMap(() => this._featureService.isEnabled$(FeatureKeys.UnifiedWorkflow)),
			take(1),
			tap(result => {
				this.isUnifiedWorkflowActive = result && ConstUtils.isModuleLevelCharType(this.charTypeId);
				const processName = this._oneConfigService.getWorkflowProcess(this.charTypeId, this.processId)?.processName;
				this.isGeneratedPaymentProcess = processName === EntityConfigUtil.GeneratedPaymentProcessName;
				if (this.isUnifiedWorkflowActive) {
					this.getRevisionStatuses(processName);
				}
			}),
			switchMap(() => this._entityConfigService.getWFStepsActionsRolesAndAssocs(this.charTypeId, this.statusId, this.processId)),
			filter(results => !!results),
			take(1),
			tap(results => {
				this.actions = results.wfActionConfig;
				this.roles = results.wfRoles;
				this.actionAssocs = results.wfActionAndStepRoleAssocsUx2Response.wfAssocAction;
				this.roleAssocs = results.wfActionAndStepRoleAssocsUx2Response.wfStepRoleAssoc;

				if (!this.isCreate) {
					this.wfStatusModel.selectedActionIDs = this.setValidActions(this.actionAssocs.map(assoc => assoc.actionID));
					this.wfStatusModel.stepRoleLabels = this.buildPreselectedAssocRolesMap();
					this.buildForm(this.wfStatusModel);
				} else {
					this.buildForm();
					this.selectAllValidActions(true);
				}
				this.formLoaded = true;
			})
		).subscribe();

		const fSub = this._featureService.isEnabled$(FeatureKeys.SummationLocking).subscribe(summationLockingEnabled => this.summationLockingEnabled = summationLockingEnabled);

		this._subs.push(sub, fSub);
	}

	setTitle() {
		this.title = this.isCreate ? "Create New Workflow Status" : `Edit Workflow Status - ${this.setStatusName}`;
	}

	buildForm(model: IWfStep = null) {
		if (model) {
			if (this.isUnifiedWorkflowActive && isNaN(model.revisionStatusIndicator)) {
				model.revisionStatusIndicator = this.getInitialRevisionStatus();
			}
			this.form = this._fb.group<IWorkflowStatusForm>({
				statusId: this._fb.control(model.stepID),
				statusName: this._fb.control(model.stepName, [Validators.required]),
				statusDescription: this._fb.control(model.stepDescription),
				activeIndicator: this._fb.control(model.activeIndicator),
				deleteIndicator: this._fb.control(model.deleteIndicator),
				lockIndicator: this._fb.control(model.lockIndicator),
				systemIndicator: this._fb.control({ value: model.systemIndicator, disabled: !this.isSuperAdmin }),
				summationLockingIndicator: this._fb.control(model.summationLockingIndicator),
				validActions: this._fb.array(model.selectedActionIDs),
				associatedRoles: this._fb.array(model.stepRoleLabels),
				revisionStatus: this._fb.control({ key: model.revisionStatusIndicator, value: null })
			});
		} else {
			const initialRevisionStatus = this.getInitialRevisionStatus();
			this.form = this._fb.group<IWorkflowStatusForm>({
				statusId: this._fb.control(-1),
				statusName: this._fb.control("", [Validators.required]),
				statusDescription: this._fb.control(""),
				activeIndicator: this._fb.control(0),
				deleteIndicator: this._fb.control(0),
				lockIndicator: this._fb.control(0),
				systemIndicator: this._fb.control({ value: 0, disabled: !this.isSuperAdmin }),
				summationLockingIndicator: this._fb.control(0),
				validActions: new FormArray<FormControl<number>>([]),
				associatedRoles: new FormArray<FormControl<IAssocRolesLabels>>([]),
				revisionStatus: this._fb.control({ key: initialRevisionStatus, value: null })
			});
		}

		this.isConfigEditLocked = model?.configEditLocked === true;
		if (this.isConfigEditLocked) {
			this.form.controls.statusName.disable();
		}
	}

	getInitialRevisionStatus(): number {
		if (this.isUnifiedWorkflowActive) {
			return this.isGeneratedPaymentProcess ? null : RevisionStatus.InSetup;
		}
		return null;
	}

	setValidActions(actionIds: number[]) {
		const ids = this.actions.map(a => a.actionID);
		this.checkedActionIds = ids.filter(id => actionIds.includes(id));
		return this.checkedActionIds;
	}

	isActionIdChecked(actionId: number) {
		return this.checkedActionIds.includes(actionId);
	}

	onValidActionChange(id: number, isChecked: boolean) {
		// A seeded action on a seeded status cannot be unchecked, but additional
		// (non-seeded) actions can be added/removed freely.
		if (!isChecked && this.isSeededActionId(id)) {
			return;
		}

		const validActionsFormArray = this.form.controls.validActions as FormArray<FormControl<number>>;

		if (isChecked) {
			validActionsFormArray.push(new FormControl<number>(id));
			if (!this.checkedActionIds.includes(id)) {
				this.checkedActionIds.push(id);
			}
		} else {
			const index = validActionsFormArray.controls.findIndex(x => x.value === id);
			validActionsFormArray.removeAt(index);
			const checkedIndex = this.checkedActionIds.indexOf(id);
			this.checkedActionIds.splice(checkedIndex, 1);
		}

		this.form.controls.validActions.updateValueAndValidity();
	}

	selectAllValidActions(isChecked: boolean) {
		const validActionsFormArray = this.form.controls.validActions as FormArray<FormControl<number>>;

		if (isChecked) {
			validActionsFormArray.clear();
			const idArray = this.actions.map(a => a.actionID);
			idArray.forEach(id => {
				validActionsFormArray.push(new FormControl<number>(id));
			});
			this.checkedActionIds = idArray;
		} else {
			// Preserve seeded actions when bulk-unchecking; only seeded ones are locked.
			const seededIds = this.actions.filter(a => a.configEditLocked).map(a => a.actionID);
			validActionsFormArray.clear();
			seededIds.forEach(id => validActionsFormArray.push(new FormControl<number>(id)));
			this.checkedActionIds = seededIds;
		}
	}

	private isSeededActionId(id: number): boolean {
		return this.actions.some(a => a.actionID === id && a.configEditLocked === true);
	}

	buildPreselectedAssocRolesMap() {
		this.preselectedAssocRoles = this.roles.filter(r => this.roleAssocs.some(ra => ra.roleID === r.roleID));
		const outputArray = [] as IAssocRolesLabels[];
		if (this.preselectedAssocRoles.length) {
			const mappedPreselected = this.preselectedAssocRoles.reduce((acc, curr) => {
				acc[curr.roleID] = curr;
				return acc;
			}, {});
			const combined = this.roleAssocs.map(r => Object.assign(r, mappedPreselected[r.roleID]));
			combined.forEach(r => {
				const idString = `${r.roleID} ${r.roleName}`;
				outputArray.push({
					id: idString,
					label: r.stepName
				});
			});
		}

		return outputArray;
	}

	omitRoleOption(id: string) {
		return this.form.controls.associatedRoles.value.map(r => r.id).includes(id);
	}

	addNewRow() {
		this.form.controls.associatedRoles.push(new FormControl<IAssocRolesLabels>({ id: "", label: "" }));
	}

	removeRow(index: number) {
		this.form.controls.associatedRoles.removeAt(index);
	}

	clearRows() {
		this.form.controls.associatedRoles.clear();
	}

	async submit($event: Event) {
		if (this.form.invalid) {
			$event.preventDefault();
		} else {
			this.isSaving = true;
			this._progressService.startProgress();
			let success = true;
			try {
				const assocRoles = this.form.controls.associatedRoles.value;
				const assocRolesDict = Object.assign({}, ...assocRoles.map(x => ({ [x.id.substring(0, x.id.indexOf(" "))]: x.label })));

				const model: IWfStepSaveRequest = {
					stepID: this.form.controls.statusId.value,
					processID: this.processId,
					divisionID: null,
					charTypeID: this.charTypeId,
					stepName: this.form.controls.statusName.value,
					stepDescription: this.form.controls.statusDescription.value,
					lockIndicator: this.form.controls.lockIndicator.value,
					activeIndicator: this.form.controls.activeIndicator.value,
					deleteIndicator: this.form.controls.deleteIndicator.value,
					sequenceNumber: this.wfStatusModel ? this.wfStatusModel.sequenceNumber : 0,
					systemIndicator: this.form.controls.systemIndicator.value,
					createdBy: this.wfStatusModel ? this.wfStatusModel.createdBy : null,
					actionName: "",
					selectedActionIDs: this.form.controls.validActions.value,
					stepRoleLabels: assocRolesDict,
					revisionStatusIndicator: this.form.controls.revisionStatus.value?.key,
					summationLockingIndicator: this.form.controls.summationLockingIndicator.value
				};

				await this._entityConfigService.saveWFStep(model).toPromise();
			} catch {
				success = false;
			} finally {
				this._progressService.endProgress();
				this.isSaving = false;

				if (success) {
					this.onComplete.next(true);
					this._growlerService.success().growl("Your changes were saved.");
				} else {
					this._growlerService.error().growl("Your changes did not save, please check the form and try again.");
				}
			}
		}
	}

	getRevisionStatuses(processName?: string) {
		EntityConfigUtil.getRevisionStatus(this.charTypeId, processName).forEach(option => {
			if (option >= 1) {
				this.revisionStatuses.items.push({ key: option, value: RevisionStatusUtil.toDisplayName(option) })
			}
		})
	}

	close() {
		this.onComplete.next(null);
	}

	ngOnDestroy(): void {
		this._subs.forEach(s => s.unsubscribe());
	}
}
